import React from "react";
import { useLocation } from "react-router-dom";

import * as Sentry from "@sentry/react";

import { ServerError } from "../pages/authPages";

class ErrorBoundaryToPass extends React.Component {
	constructor(props) {
		super(props);

		this.state = { hasError: false, error: null, eventId: null };
	}

	static getDerivedStateFromError(error) {
		// Update state so the next render shows the fallback UI.
		// Must stay side-effect-free — reporting happens in componentDidCatch.
		return { hasError: true, error };
	}

	componentDidCatch(error, errorInfo) {
		// Tag with the current route for cleaner Sentry grouping, capture with
		// the React component stack (via captureReactException), and keep the
		// returned event id to show the user as a support reference.
		// NOTE: previously this called an unimported `Sentry`, which threw
		// inside componentDidCatch and lost the error entirely.
		const eventId = Sentry.withScope((scope) => {
			scope.setTag("route", this.props.location?.pathname);
			return Sentry.captureReactException(error, errorInfo);
		});

		this.setState({ eventId });
	}

	componentDidUpdate(prevProps) {
		// Clear the error when the user navigates to a different location.
		if (
			this.state.hasError &&
			prevProps.location?.pathname !== this.props.location?.pathname
		) {
			this.setState({ hasError: false, error: null, eventId: null });
		}
	}

	render() {
		if (this.state.hasError) {
			// Already reported above (with component stack + route tag); tell
			// the view not to re-capture, and surface the event id.
			return (
				<ServerError
					error={this.state.error}
					eventId={this.state.eventId}
					report={false}
				/>
			);
		}

		return this.props.children;
	}
}

export const ErrorBoundary = ({ children }) => {
	const location = useLocation();

	return (
		<ErrorBoundaryToPass location={location}>
			{children}
		</ErrorBoundaryToPass>
	);
};
